import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import { toast } from "react-toastify";

// Create a context for notifications
const NotificationContext = createContext();

// Utility function to load from localStorage
const loadFromLocalStorage = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch (error) {
    console.error("Error loading from local storage:", error);
    return defaultValue; // Return the default value if parsing fails
  }
};

// Utility function to save to localStorage
const saveToLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Error saving to local storage:", error);
  }
};

// Provide this context to the rest of the app
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState(() =>
    loadFromLocalStorage("notifications1", [])
  );

  const [unreadCount, setUnreadCount] = useState(() =>
    notifications && Array.isArray(notifications)
      ? notifications.filter((n) => !n.read).length
      : 0
  );

  const [notifiedMachines, setNotifiedMachines] = useState(() =>
    new Set(loadFromLocalStorage("notifiedMachines1", []))
  );

  const prevDeploymentsRef = useRef([]); // Store previous deployments

  // Sync notifications and notifiedMachines to localStorage whenever they change
  useEffect(() => {
    saveToLocalStorage("notifications1", notifications);
    saveToLocalStorage("notifiedMachines1", Array.from(notifiedMachines)); // Store as array
  }, [notifications, notifiedMachines]);

  const addNotification = (notification) => {
    if (!notification.machineName || notifiedMachines.has(notification.id)) {
      return;
    }

    // Add the notification to the state
    setNotifications((prev) => (Array.isArray(prev) ? [...prev, notification] : [notification]));
    setNotifiedMachines((prev) => new Set(prev).add(notification.id)); // Mark notification ID as notified

    // Update unread count
    if (!notification.read) {
      setUnreadCount((prev) => prev + 1);
    }

    // Display the toast notification
    toast.info(
      <>
        <strong>{notification.title}</strong>
        <div>{notification.content}</div>
      </>,
      {
        position: 'bottom-right',
        autoClose: 5000, // Closes after 5 seconds
      }
    );
  };

  const markAsRead = (id) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
    setUnreadCount((prev) => Math.max(prev - 1, 0));
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    setNotifiedMachines(new Set()); // Clear notified machines on reset
  };

  const markAllAsRead = () => {
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, read: true }))
    );
    setUnreadCount(0);
  };

  const handleDeploymentNotifications = (deployments, showBrowserNotification) => {
    const prevDeployments = prevDeploymentsRef.current;
  
    const newDeployments = deployments.filter(
      (deployment) =>
        !prevDeployments.some((prev) => prev.id === deployment.id) && // New deployment not in previous state
        !deployment.isArchive // Exclude archived deployments
    );
  
    // Notify about new deployments (start)
    newDeployments.forEach((deployment) => {
      const title = "New Deployment Detected";
      const message = `New deployment for ${deployment.machineName} has been started.`;

      const id = `start-${deployment.machineName}`; // Unique ID for start
  
      showBrowserNotification(title, {
        body: message,
        icon: "/JimmyBot.webp", // Replace with your icon path
      });
  
      addNotification({
        id, // Unique ID for the started deployment
        title: "New Deployment Detected",
        content: `New deployment for ${deployment.machineName} has been started.`,
        timestamp: Date.now(),
        read: false,
        machineName: deployment.machineName, // Store machineName for tracking
      });
    });
  
    // Notify about running deployments that have just completed
    deployments.forEach((deployment) => {
      const previousDeployment = prevDeployments.find((prev) => prev.id === deployment.id);
  
      // Check if this deployment was running but is now complete
      if (
        previousDeployment &&
        previousDeployment.percentComplete < 100 && // Was running
        deployment.percentComplete === 100 && // Now completed
        !deployment.isArchive && // Not archived
        !notifiedMachines.has(`completed-${deployment.machineName}`) // Ensure no duplicate notifications
      ) {
        const title = "Deployment Completed";
        const message = `Deployment for ${deployment.machineName} has completed successfully.`;
  
        const id = `completed-${deployment.machineName}`; // Unique ID for completion
  
        showBrowserNotification(title, {
          body: message,
          icon: "/JimmyBot.webp", // Replace with your icon path
        });
  
        addNotification({
          id, // Unique ID for the completed deployment
          title: "Deployment Completed",
          content: `Deployment for ${deployment.machineName} has completed successfully.`,
          timestamp: Date.now(),
          read: false,
          machineName: deployment.machineName, // Store machineName for tracking
        });
  
        // Mark this deployment as notified for completion
        setNotifiedMachines((prev) => new Set(prev).add(`completed-${deployment.machineName}`));
      }
    });
  
    // Update previous deployments reference
    prevDeploymentsRef.current = deployments;
  };
  

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        clearNotifications,
        markAllAsRead,
        notifiedMachines, // Expose notified machines for further use
        handleDeploymentNotifications, // Handle new/complete deployment notifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotifications = () => useContext(NotificationContext);
